import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Download, Package, RefreshCw, Truck } from 'lucide-react';
import api, { mediaUrl } from '../../lib/api';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../hooks/use-toast';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-red-100 text-red-700',
};

const MyOrders = () => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/shop/my-orders').then((r) => setOrders(r.data || [])).catch((e) => { console.error('Failed to load orders:', e); setOrders([]); }).finally(() => setLoading(false));
  }, []);

  const buyAgain = (order) => {
    order.items.forEach((it) => addItem({ id: it.product_id, slug: it.slug, title: it.title, price: it.price, image: it.image, product_type: it.product_type }, it.quantity));
    toast({ title: 'Added to cart' });
    navigate('/cart');
  };

  return (
    <div className="space-y-6">
      <div><div className="eyebrow mb-2">My Account</div><h1 className="font-serif-display text-4xl text-burgundy">My <span className="font-italic-display text-rose-italic">Orders</span></h1></div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : orders.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="my-orders-empty"><Package className="w-8 h-8 mx-auto mb-2 opacity-50" />No orders yet.</div>
        : (
          <ul className="space-y-4" data-testid="my-orders-list">
            {orders.map((o) => (
              <li key={o.session_id} className="p-5 rounded-2xl border border-rose/50 bg-white/70" data-testid={`my-order-${o.order_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-rose/30">
                  <div>
                    <span className="font-serif-display text-lg text-burgundy">{o.order_number || 'Order'}</span>
                    <span className="text-xs text-mahogany/60 ml-2">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] px-2.5 py-0.5 rounded-full capitalize ${STATUS_STYLE[o.status] || 'bg-gray-100 text-gray-700'}`}>{o.status}</span>
                    <span className="text-mahogany font-medium">${(o.total || 0).toFixed(2)}</span>
                  </div>
                </div>
                <ul className="py-3 space-y-2">
                  {o.items.map((it, i) => (
                    <li key={it.product_id || i} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-burgundy">{it.title} <span className="text-mahogany/60">× {it.quantity}</span>{it.product_type === 'digital' && <span className="ml-2 text-[10px] uppercase px-2 py-0.5 rounded-full bg-mahogany text-cream">Digital</span>}</span>
                      {it.digital_file_url && <a href={mediaUrl(it.digital_file_url)} target="_blank" rel="noreferrer" download className="pill-btn py-1 text-xs" data-testid={`redownload-${o.order_number}-${i}`}>Download <Download className="w-3.5 h-3.5" /></a>}
                    </li>
                  ))}
                </ul>
                {o.tracking_number && (
                  <div className="flex items-center gap-2 text-sm text-mahogany/80 bg-cream-2/60 rounded-lg px-3 py-2 mb-2"><Truck className="w-4 h-4" /> {o.carrier || 'Tracking'}: <span className="font-medium text-burgundy">{o.tracking_number}</span></div>
                )}
                {o.shipping_address?.line1 && (
                  <div className="text-xs text-mahogany/60">Ship to: {[o.shipping_address.line1, o.shipping_address.city, o.shipping_address.country].filter(Boolean).join(', ')}</div>
                )}
                <button onClick={() => buyAgain(o)} className="mt-3 pill-btn-outline text-sm" data-testid={`buy-again-${o.order_number}`}><RefreshCw className="w-3.5 h-3.5" /> Buy Again</button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

export default MyOrders;
